import { DETAILED_LOG_DAYS, MAX_LOG_PER_DAY } from './constants';
import type { DayKey, DayRecord, SessionEntry, StreakCarry } from './types';
import { addDays, dayKey } from './util';

export type History = Record<DayKey, DayRecord>;

export const emptyDay = (): DayRecord => ({ sessions: 0, minutes: 0, interruptions: 0, log: [] });

export const getDay = (history: History, key: DayKey): DayRecord => history[key] ?? emptyDay();

/** Adds a completed focus session to the day it ended on. Mutates `history`. */
export function recordSession(history: History, entry: SessionEntry): DayRecord {
  const key = dayKey(entry.ts);
  const day = (history[key] ??= emptyDay());
  day.sessions += 1;
  day.minutes += entry.min;
  day.interruptions += entry.interruptions;
  day.log.unshift(entry);
  day.log.sort((a, b) => b.ts - a.ts);
  if (day.log.length > MAX_LOG_PER_DAY) day.log.length = MAX_LOG_PER_DAY;
  return day;
}

/** Drops detailed logs older than the retention window; aggregates are kept. Mutates `history`. */
export function compact(history: History, now: number, keepDays = DETAILED_LOG_DAYS): void {
  const cutoff = addDays(dayKey(now), -keepDays);
  for (const [k, d] of Object.entries(history)) {
    if (d.sessions === 0 && d.interruptions === 0) delete history[k];
    else if (k < cutoff && d.log.length) d.log = [];
  }
}

const isActive = (history: History, k: DayKey) => (history[k]?.sessions ?? 0) > 0;

/** Length of the run of active days ending at `end`, honouring a migrated legacy streak. */
function runEndingAt(history: History, carry: StreakCarry | null, end: DayKey): number {
  let n = 0;
  for (let k = end; ; k = addDays(k, -1)) {
    if (carry && k === carry.lastDay) return n + carry.streak;
    if (!isActive(history, k)) return n;
    n++;
  }
}

/** Consecutive active days up to today (today may still be empty without breaking the streak). */
export function currentStreak(history: History, carry: StreakCarry | null, now: number): number {
  const today = dayKey(now);
  const counted = isActive(history, today) || carry?.lastDay === today;
  return runEndingAt(history, carry, counted ? today : addDays(today, -1));
}

export function bestStreak(history: History, carry: StreakCarry | null): number {
  let best = carry?.streak ?? 0;
  for (const k of Object.keys(history)) {
    if (!isActive(history, k) || isActive(history, addDays(k, 1))) continue; // only run ends
    best = Math.max(best, runEndingAt(history, carry, k));
  }
  return best;
}

export interface DaySummary {
  key: DayKey;
  sessions: number;
  minutes: number;
}

/** The last `n` calendar days ending today, oldest first, gaps filled with zeros. */
export function lastNDays(history: History, n: number, now: number): DaySummary[] {
  const today = dayKey(now);
  const out: DaySummary[] = [];
  for (let i = n - 1; i >= 0; i--) {
    const key = addDays(today, -i);
    const d = history[key];
    out.push({ key, sessions: d?.sessions ?? 0, minutes: d?.minutes ?? 0 });
  }
  return out;
}
